import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { ProjectSelect } from '../components/ProjectSelect';
import { useAuth } from '../contexts/useAuth';
import { hoursToDays } from '../lib/utils';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    Save,
    Trash2,
    Loader2,
    Users,
    Filter,
    Unlock,
    CheckSquare,
    Send,
    CheckCircle,
} from 'lucide-react';
import type { ApiResponse, TimeEntry, Project, User } from '../types';

export function Collab() {
    const api = useApi();
    const { isAdmin } = useAuth();
    const queryClient = useQueryClient();

    const [selectedWeek, setSelectedWeek] = useState(new Date());
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [showForm, setShowForm] = useState(false);
    const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
    const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set());

    // Calculate week bounds
    const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
    const startDateStr = format(weekStart, 'yyyy-MM-dd');
    const endDateStr = format(weekEnd, 'yyyy-MM-dd');

    // Fetch all users
    const { data: usersData } = useQuery({
        queryKey: ['users', 'active'],
        queryFn: () =>
            api.get<ApiResponse<User[]>>('/api/users', { active: true }),
    });

    // Fetch all entries for the week (admin can see all)
    const { data: entriesData, isLoading } = useQuery({
        queryKey: ['time-entries', 'all', startDateStr, endDateStr, selectedUserId],
        queryFn: () =>
            api.get<ApiResponse<TimeEntry[]>>('/api/time-entries', {
                startDate: startDateStr,
                endDate: endDateStr,
                ...(selectedUserId && { userId: selectedUserId }),
            }),
    });

    // Fetch projects for the form
    const { data: projectsData } = useQuery({
        queryKey: ['projects', 'active'],
        queryFn: () =>
            api.get<ApiResponse<Project[]>>('/api/projects', { active: true }),
    });

    const users = usersData?.data || [];
    const entries = entriesData?.data || [];
    const projects = projectsData?.data || [];

    // Group entries by user
    const entriesByUser = entries.reduce((acc, entry) => {
        const userId = entry.userId;
        if (!acc[userId]) {
            acc[userId] = {
                userName: entry.userName || 'Inconnu',
                userEmail: entry.userEmail || '',
                entries: [],
            };
        }
        acc[userId].entries.push(entry);
        return acc;
    }, {} as Record<string, { userName: string; userEmail: string; entries: TimeEntry[] }>);

    // Admin batch status change mutation
    const changeStatusMutation = useMutation({
        mutationFn: ({ entryIds, targetStatus }: { entryIds: string[]; targetStatus: string }) =>
            api.post('/api/time-entries/batch/change-status', { entryIds, targetStatus }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
            setSelectedEntryIds(new Set());
        },
    });

    const toggleEntrySelection = (entryId: string) => {
        setSelectedEntryIds((prev) => {
            const next = new Set(prev);
            if (next.has(entryId)) {
                next.delete(entryId);
            } else {
                next.add(entryId);
            }
            return next;
        });
    };

    const toggleAllUserEntries = (userEntries: TimeEntry[]) => {
        const eligibleIds = userEntries
            .filter((e) => ['draft', 'submitted', 'rejected'].includes(e.status))
            .map((e) => e.id);
        const allSelected = eligibleIds.every((id) => selectedEntryIds.has(id));
        setSelectedEntryIds((prev) => {
            const next = new Set(prev);
            eligibleIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
            return next;
        });
    };

    const selectAllEntries = () => {
        const eligibleIds = entries
            .filter((e) => ['draft', 'submitted', 'rejected'].includes(e.status))
            .map((e) => e.id);
        const allSelected = eligibleIds.every((id) => selectedEntryIds.has(id));
        if (allSelected) {
            setSelectedEntryIds(new Set());
        } else {
            setSelectedEntryIds(new Set(eligibleIds));
        }
    };

    const navigateWeek = (direction: 'prev' | 'next') => {
        setSelectedWeek(direction === 'prev' ? subWeeks(selectedWeek, 1) : addWeeks(selectedWeek, 1));
    };

    const openEditEntry = (entry: TimeEntry) => {
        setEditingEntry(entry);
        setShowForm(true);
    };

    const dateInputRef = useRef<HTMLInputElement>(null);

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            const newDate = new Date(e.target.value + 'T12:00:00');
            setSelectedWeek(newDate);
        }
    };

    const openDatePicker = () => {
        dateInputRef.current?.showPicker();
    };

    // Status helpers
    const statusColors: Record<string, string> = {
        draft: 'badge-draft',
        submitted: 'badge-submitted',
        approved: 'badge-approved',
        rejected: 'badge-rejected',
        billed: 'badge-billed',
    };

    const statusLabels: Record<string, string> = {
        draft: 'Brouillon',
        submitted: 'Soumis',
        approved: 'Validé',
        rejected: 'Rejeté',
        billed: 'Facturé',
    };

    // Calculate totals
    const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);
    const usersWithEntries = Object.keys(entriesByUser).length;

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Users className="w-6 h-6 text-[var(--color-primary)]" />
                        <h1 className="text-2xl font-bold text-[var(--color-text)]">
                            CRA Collaborateurs
                        </h1>
                    </div>
                    {isAdmin && entries.length > 0 && (
                        <button
                            onClick={selectAllEntries}
                            className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                        >
                            <CheckSquare className="w-4 h-4" />
                            {entries.filter((e) => ['draft', 'submitted', 'rejected'].includes(e.status)).every((e) => selectedEntryIds.has(e.id)) && selectedEntryIds.size > 0
                                ? 'Tout désélectionner'
                                : 'Tout sélectionner'}
                        </button>
                    )}
                </div>

                {/* Week navigation */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigateWeek('prev')}
                            className="p-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="relative">
                            <input
                                ref={dateInputRef}
                                type="date"
                                value={format(selectedWeek, 'yyyy-MM-dd')}
                                onChange={handleDateChange}
                                className="sr-only"
                            />
                            <button
                                type="button"
                                onClick={openDatePicker}
                                className="flex items-center gap-3 px-4 py-2 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg cursor-pointer hover:border-[var(--color-primary)] transition-colors"
                            >
                                <Calendar className="w-5 h-5 text-[var(--color-primary)]" />
                                <div className="text-left">
                                    <p className="font-semibold text-[var(--color-text)]">
                                        {format(weekStart, 'd MMM', { locale: fr })} - {format(weekEnd, 'd MMM yyyy', { locale: fr })}
                                    </p>
                                </div>
                            </button>
                        </div>

                        <button
                            onClick={() => navigateWeek('next')}
                            className="p-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* User filter */}
                    <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                        <Filter className="w-4 h-4 text-[var(--color-text-secondary)]" />
                        <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="input flex-1 sm:w-64"
                        >
                            <option value="">Tous les collaborateurs</option>
                            {users.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.displayName || user.email}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="card">
                    <p className="text-sm text-[var(--color-text-secondary)]">Collaborateurs</p>
                    <p className="text-2xl font-bold text-[var(--color-text)]">{usersWithEntries}</p>
                </div>
                <div className="card">
                    <p className="text-sm text-[var(--color-text-secondary)]">Total saisies</p>
                    <p className="text-2xl font-bold text-[var(--color-text)]">{entries.length}</p>
                </div>
                <div className="card col-span-2 sm:col-span-1">
                    <p className="text-sm text-[var(--color-text-secondary)]">Total jours</p>
                    <p className="text-2xl font-bold text-[var(--color-primary)]">{hoursToDays(totalHours)}j</p>
                </div>
            </div>

            {/* Admin bulk actions */}
            {isAdmin && selectedEntryIds.size > 0 && (
                <div className="flex flex-wrap items-center gap-3 p-4 bg-[var(--color-primary-light)] border border-[var(--color-primary)] rounded-lg">
                    <CheckSquare className="w-5 h-5 text-[var(--color-primary)]" />
                    <span className="font-medium text-[var(--color-text)]">
                        {selectedEntryIds.size} saisie(s) sélectionnée(s)
                    </span>
                    <div className="flex items-center gap-2 ml-auto">
                        <button
                            onClick={() =>
                                changeStatusMutation.mutate({
                                    entryIds: Array.from(selectedEntryIds),
                                    targetStatus: 'submitted',
                                })
                            }
                            disabled={changeStatusMutation.isPending}
                            className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                        >
                            <Send className="w-3.5 h-3.5" />
                            Passer en "À valider"
                        </button>
                        <button
                            onClick={() =>
                                changeStatusMutation.mutate({
                                    entryIds: Array.from(selectedEntryIds),
                                    targetStatus: 'approved',
                                })
                            }
                            disabled={changeStatusMutation.isPending}
                            className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1"
                        >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Valider
                        </button>
                        <button
                            onClick={() => setSelectedEntryIds(new Set())}
                            className="btn-secondary text-sm py-1.5 px-3"
                        >
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            {/* Entries by user */}
            <div className="space-y-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                    </div>
                ) : Object.keys(entriesByUser).length === 0 ? (
                    <div className="text-center py-12 card">
                        <p className="text-[var(--color-text-secondary)]">
                            Aucune saisie pour cette période
                        </p>
                    </div>
                ) : (
                    Object.entries(entriesByUser).map(([userId, userData]) => {
                        const userTotalDays = hoursToDays(
                            userData.entries.reduce((sum, e) => sum + e.hours, 0)
                        );
                        const eligibleUserEntries = userData.entries.filter((e) =>
                            ['draft', 'submitted', 'rejected'].includes(e.status)
                        );
                        const allUserSelected =
                            eligibleUserEntries.length > 0 &&
                            eligibleUserEntries.every((e) => selectedEntryIds.has(e.id));

                        return (
                            <div key={userId} className="card">
                                {/* User header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 pb-4 border-b border-[var(--color-border)]">
                                    <div className="flex items-center gap-3">
                                        {isAdmin && eligibleUserEntries.length > 0 && (
                                            <input
                                                type="checkbox"
                                                checked={allUserSelected}
                                                onChange={() => toggleAllUserEntries(userData.entries)}
                                                className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer"
                                                title="Sélectionner toutes les saisies de ce collaborateur"
                                            />
                                        )}
                                        <div>
                                            <h3 className="font-semibold text-[var(--color-text)]">
                                                {userData.userName}
                                            </h3>
                                            <p className="text-sm text-[var(--color-text-secondary)]">
                                                {userData.userEmail}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xl font-bold text-[var(--color-primary)]">
                                            {userTotalDays}j
                                        </p>
                                        <p className="text-xs text-[var(--color-text-secondary)]">
                                            {userData.entries.length} saisie(s)
                                        </p>
                                    </div>
                                </div>

                                {/* User entries */}
                                <div className="space-y-3">
                                    {userData.entries.map((entry) => (
                                        <CollabEntryCard
                                            key={entry.id}
                                            entry={entry}
                                            onEdit={() => openEditEntry(entry)}
                                            statusColors={statusColors}
                                            statusLabels={statusLabels}
                                            isAdmin={isAdmin}
                                            isSelected={selectedEntryIds.has(entry.id)}
                                            onToggleSelect={() => toggleEntrySelection(entry.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Entry Form Modal */}
            {showForm && (
                <EntryFormModal
                    entry={editingEntry}
                    projects={projects}
                    onClose={() => {
                        setShowForm(false);
                        setEditingEntry(null);
                    }}
                    onSaved={() => {
                        setShowForm(false);
                        setEditingEntry(null);
                        queryClient.invalidateQueries({ queryKey: ['time-entries'] });
                    }}
                />
            )}
        </div>
    );
}

interface CollabEntryCardProps {
    entry: TimeEntry;
    onEdit: () => void;
    statusColors: Record<string, string>;
    statusLabels: Record<string, string>;
    isAdmin: boolean;
    isSelected: boolean;
    onToggleSelect: () => void;
}

function CollabEntryCard({ entry, onEdit, statusColors, statusLabels, isAdmin, isSelected, onToggleSelect }: CollabEntryCardProps) {
    const api = useApi();
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: () => api.delete(`/api/time-entries/${entry.id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const unlockMutation = useMutation({
        mutationFn: () => api.post(`/api/time-entries/${entry.id}/unlock`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const canModify = ['draft', 'submitted', 'rejected'].includes(entry.status);
    const canUnlock = ['approved', 'billed'].includes(entry.status);
    const canSelect = ['draft', 'submitted', 'rejected'].includes(entry.status);

    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-[var(--color-bg)] rounded-lg">
            <div className="flex items-start gap-3 flex-1 min-w-0">
                {isAdmin && canSelect && (
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onToggleSelect}
                        className="w-4 h-4 mt-1 accent-[var(--color-primary)] cursor-pointer flex-shrink-0"
                    />
                )}
                {isAdmin && !canSelect && <div className="w-4 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-sm text-[var(--color-text-secondary)]">
                            {format(new Date(entry.entryDate), 'EEE d MMM', { locale: fr })}
                        </span>
                        <span className="font-medium text-[var(--color-text)]">
                            {entry.projectCode}
                        </span>
                        <span className={statusColors[entry.status]}>
                            {statusLabels[entry.status]}
                        </span>
                    </div>
                    <p className="text-sm text-[var(--color-text-secondary)] truncate">
                        {entry.projectName}
                    </p>
                    {entry.description && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">
                            {entry.description}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-[var(--color-text)]">
                    {hoursToDays(entry.hours)}j
                </span>

                {canUnlock && (
                    <button
                        onClick={() => unlockMutation.mutate()}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                        disabled={unlockMutation.isPending}
                        title="Déverrouiller pour permettre la modification"
                    >
                        <Unlock className="w-3.5 h-3.5" />
                        Déverrouiller
                    </button>
                )}

                {canModify && (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => deleteMutation.mutate()}
                            className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-light)] rounded-lg transition-colors"
                            disabled={deleteMutation.isPending}
                            title="Supprimer"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onEdit}
                            className="btn-secondary text-sm py-1.5 px-3"
                        >
                            Modifier
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

interface EntryFormModalProps {
    entry: TimeEntry | null;
    projects: Project[];
    onClose: () => void;
    onSaved: () => void;
}

function EntryFormModal({ entry, projects, onClose, onSaved }: EntryFormModalProps) {
    const api = useApi();
    const [formData, setFormData] = useState({
        projectId: entry?.projectId || '',
        days: entry?.hours ? (entry.hours / 8).toString() : '',
        description: entry?.description || '',
        taskType: entry?.taskType || '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Un projet clôturé n'accepte plus de saisie : le serveur la refuse, autant ne pas la
    // proposer. Cet écran liste tous les projets actifs, sans le filtre de « mes projets »
    // qui écarte déjà les projets clos.
    const openProjects = projects.filter((project) => !project.closed);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        if (!formData.projectId) {
            setError('Veuillez choisir un projet');
            setIsSubmitting(false);
            return;
        }

        const hoursToStore = parseFloat(formData.days) * 8;

        try {
            if (entry) {
                await api.put(`/api/time-entries/${entry.id}`, {
                    projectId: formData.projectId,
                    description: formData.description,
                    taskType: formData.taskType,
                    hours: hoursToStore,
                });
            }
            onSaved();
        } catch (err) {
            setError((err as Error).message || 'Une erreur est survenue');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card w-full max-w-md shadow-xl">
                <div className="-m-6 mb-4 p-6 border-b border-[var(--color-border)]">
                    <h2 className="text-lg font-semibold text-[var(--color-text)]">
                        Modifier la saisie
                    </h2>
                    {entry && (
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                            {entry.userName} - {format(new Date(entry.entryDate), 'd MMMM yyyy', { locale: fr })}
                        </p>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="p-3 bg-[var(--color-error-light)] border border-[var(--color-error)] rounded-lg text-[var(--color-error)] text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Projet *
                        </label>
                        {/* Le projet de la saisie reste proposé même clôturé, sinon le champ
                            paraîtrait vide sur une ligne ancienne. */}
                        <ProjectSelect
                            projects={openProjects}
                            value={formData.projectId}
                            onChange={(projectId) => setFormData({ ...formData, projectId })}
                            extra={
                                entry?.projectId
                                    ? {
                                        id: entry.projectId,
                                        label: `${entry.projectCode ? entry.projectCode + ' - ' : ''}${entry.projectName || 'Projet clôturé'}`,
                                        note: 'saisie fermée',
                                    }
                                    : null
                            }
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Jours *
                        </label>
                        <input
                            type="number"
                            step="0.25"
                            min="0.25"
                            max="2"
                            value={formData.days}
                            onChange={(e) => setFormData({ ...formData, days: e.target.value })}
                            className="input"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Type de tâche
                        </label>
                        <input
                            type="text"
                            value={formData.taskType}
                            onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
                            placeholder="Ex: Développement, Réunion, Analyse..."
                            className="input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            placeholder="Décrivez brièvement le travail effectué..."
                            className="input"
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary justify-center order-2 sm:order-1"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-primary justify-center order-1 sm:order-2"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Enregistrer
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
